'use client'

import { useEffect, useMemo, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Plus, Search } from 'lucide-react'
import z from 'zod/v4'

import type { BookDetails } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { BookGalleryCard } from '../components/book-gallery-card'
import { BookCoverCard } from '../components/cards/book-cover-card'
import { useSharedTRPC } from '../context'
import { MyBookCollectionForm } from './my-book-collection'

const FormSchema = z.object({
  selectedCollections: z.array(z.string()).optional(),
})

export type CollectionFormValues = z.infer<typeof FormSchema>

interface PutBooksInMyCollectionFormProps {
  bookId: string
  className?: string
  onSuccess?: () => void
  onCancel?: () => void
  renderCollectionLink: RenderLink
}

export function PutBooksInMyCollectionForm({
  bookId,
  className,
  onSuccess,
  onCancel,
  renderCollectionLink,
}: PutBooksInMyCollectionFormProps) {
  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const [searchQuery, setSearchQuery] = useState('')
  const [openMakeNewBookCollectionDialog, setOpenMakeNewBookCollectionDialog] = useState(false)

  // Collections created from within this form that should stay checked even after the list refetches.
  const [autoCheckedIds, setAutoCheckedIds] = useState<string[]>([])

  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, { wait: 500 })

  const {
    data: collectionData,
    isPending: isCollectionLoading,
    isError: isCollectionError,
    refetch: refetchCollections,
  } = useQuery(
    trpc.myBookCollection.getAllBookCollections.queryOptions({
      search: debouncedSearchQuery,
      pageSize: 10,
    }),
  )

  const {
    data: bookData,
    isPending: isBookLoading,
    isError: isBookError,
    refetch: refetchBook,
  } = useQuery(trpc.library.getBookDetails.queryOptions({ id: bookId }))

  const { mutateAsync: updateBookCollection } = useMutation(
    trpc.myBookCollection.updateBookCollection.mutationOptions(),
  )

  const form = useForm({
    defaultValues: {
      selectedCollections: [],
    } as z.input<typeof FormSchema>,
    validators: {
      onSubmit: FormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const isRemove = value.selectedCollections === undefined || value.selectedCollections.length === 0

      const toastId = toast.loading(
        isRemove
          ? 'Menghapus buku dari koleksi...'
          : `Memasukkan buku ke ${value.selectedCollections?.length} koleksi...`,
      )

      try {
        if (!bookData) throw new Error('Book data not found')

        if (!collectionData) throw new Error('Collection data not found')

        const allCollections = collectionData.data
        const selectedIds = new Set(value.selectedCollections ?? [])

        const mutations: Promise<unknown>[] = []

        for (const collection of allCollections) {
          const hadBefore = collection.myBookCollectionToBooks.some((row) => row.book.id === bookData.id)
          const hasNow = selectedIds.has(collection.id)

          if (hadBefore === hasNow) continue

          const existingBookIds = collection.myBookCollectionToBooks.map((row) => row.book.id)
          const nextBookIds = hasNow
            ? Array.from(new Set([...existingBookIds, bookData.id]))
            : existingBookIds.filter((id) => id !== bookData.id)

          mutations.push(
            updateBookCollection({
              id: collection.id,
              bookIds: nextBookIds,
            }),
          )
        }

        if (mutations.length > 0) {
          await Promise.all(mutations)

          await queryClient.invalidateQueries({
            queryKey: trpc.myBookCollection.getAllBookCollections.infiniteQueryKey(),
          })
        }

        if (isRemove || !value.selectedCollections || value.selectedCollections.length === 0) {
          toast.custom(() => <CollectionToast bookData={bookData} data={{ type: 'delete' }} />)
        } else if (value.selectedCollections.length === 1) {
          const selectedId = value.selectedCollections[0]
          const collection = allCollections.find((c) => c.id === selectedId)

          if (!collection) throw new Error('Collection not found')

          toast.custom(() => (
            <CollectionToast
              bookData={bookData}
              data={{
                type: 'create',
                createType: 'one',
                renderLink: renderCollectionLink,
                collectionName: collection.name,
                collectionId: collection.id,
              }}
            />
          ))
        } else {
          toast.custom(() => (
            <CollectionToast
              bookData={bookData}
              data={{
                type: 'create',
                createType: 'multiple',
                collectionLength: value.selectedCollections ? value.selectedCollections.length : 0,
                renderLink: renderCollectionLink,
              }}
            />
          ))
        }

        formApi.reset()

        if (onSuccess) onSuccess()
      } catch {
        toast.error(isRemove ? 'Gagal menghapus buku dari koleksi!' : 'Gagal memasukkan buku ke koleksi!')
      } finally {
        toast.dismiss(toastId)
      }
    },
  })

  const selectedCollections = useStore(form.store, (state) => state.values.selectedCollections ?? [])

  const filteredCollections = useMemo(() => {
    if (!collectionData) return []
    return collectionData.data
  }, [collectionData, searchQuery])

  useEffect(() => {
    if (bookData && collectionData) {
      const preSelectedCollections = collectionData.data
        .filter((collection) => collection.myBookCollectionToBooks.some((row) => row.book.id === bookData.id))
        .map((collection) => collection.id.toString())

      const merged = Array.from(new Set([...preSelectedCollections, ...autoCheckedIds])).filter((id) =>
        collectionData.data.some((collection) => collection.id.toString() === id),
      )

      form.setFieldValue('selectedCollections', merged)
    }
  }, [bookData, collectionData, form, autoCheckedIds])

  const loading = useStore(form.store, (state) => state.isSubmitting)

  const disabled = loading

  if (isBookError) {
    return (
      <section
        className={cn(
          'srd:flex srd:flex-col srd:items-center srd:justify-center srd:w-full srd:gap-y-4 srd:py-14',
          className,
        )}
      >
        <p className={cn(typographyVariants({ variant: 'title-lg' }))}>Oops, Terjadi Kesalahan</p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }))}>Gagal memuat data koleksi. Silakan coba lagi.</p>
        <Button
          onClick={() => {
            void refetchCollections()
            void refetchBook()
          }}
        >
          Coba Lagi
        </Button>
      </section>
    )
  }

  if (isBookLoading) {
    return (
      <div className={cn('srd:flex srd:flex-col srd:gap-y-4 srd:w-full', className)}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={`skeleton-${index}`} className='srd:w-full srd:h-[100px] srd:rounded-lg' />
        ))}
      </div>
    )
  }

  const disabledButton =
    JSON.stringify(
      filteredCollections
        .filter((collection) => collection.myBookCollectionToBooks.some((row) => row.book.id === bookData.id))
        .map((collection) => collection.id.toString())
        .sort(),
    ) === JSON.stringify([...selectedCollections].sort())

  return (
    <>
      <section className={cn('srd:flex srd:flex-col srd:gap-y-6 srd:w-full srd:min-w-0 srd:max-h-[80dvh]', className)}>
        <div className='srd:flex srd:items-center srd:w-full srd:justify-center'>
          <BookCoverCard coverUrl={bookData.coverUrl} size='small' />
        </div>

        <div className='srd:w-full srd:flex srd:items-center srd:gap-4 srd:flex-wrap'>
          <InputGroup className='srd:flex-1 srd:min-w-[300px]' size='sm'>
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              type='text'
              placeholder='Cari'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          <Button
            variant='ghost'
            onClick={() => setOpenMakeNewBookCollectionDialog(true)}
            className='srd:w-fit srd:h-fit srd:p-0'
          >
            <Plus className='srd:size-4 srd:mr-2' />
            Buat baru
          </Button>
        </div>

        {(() => {
          if (isCollectionError) {
            return (
              <section
                className={cn(
                  'srd:flex srd:flex-col srd:items-center srd:justify-center srd:w-full srd:gap-y-4 srd:py-14',
                  className,
                )}
              >
                <p className={cn(typographyVariants({ variant: 'title-lg' }))}>Oops, Terjadi Kesalahan</p>
                <p className={cn(typographyVariants({ variant: 'body-sm' }))}>
                  Gagal memuat data koleksi. Silakan coba lagi.
                </p>
                <Button
                  onClick={() => {
                    void refetchCollections()
                    void refetchBook()
                  }}
                >
                  Coba Lagi
                </Button>
              </section>
            )
          }

          if (isCollectionLoading) {
            return (
              <div className={cn('srd:flex srd:flex-col srd:gap-y-4 srd:w-full', className)}>
                {Array.from({ length: 2 }).map((_, index) => (
                  <Skeleton key={`skeleton-${index}`} className='srd:w-full srd:h-[100px] srd:rounded-lg' />
                ))}
              </div>
            )
          }

          if (filteredCollections.length === 0) {
            return (
              <div className={cn('srd:flex srd:flex-col srd:gap-y-4 srd:w-full', className)}>
                <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:py-14')}>
                  {debouncedSearchQuery.length > 0
                    ? `Tidak ada koleksi yang ditemukan dengan nama ${debouncedSearchQuery}.`
                    : 'Tidak ada koleksi yang ditemukan. Silakan buat koleksi baru.'}
                </p>

                <div className='srd:flex srd:items-center srd:justify-end srd:gap-x-4'>
                  <Button
                    variant='ghost'
                    disabled={disabled}
                    onClick={() => {
                      const preSelectedCollections = collectionData.data
                        .filter((collection) =>
                          collection.myBookCollectionToBooks.some((row) => row.book.id === bookData.id),
                        )
                        .map((collection) => collection.id.toString())

                      form.setFieldValue('selectedCollections', preSelectedCollections)

                      if (onCancel) onCancel()
                    }}
                  >
                    Batal
                  </Button>

                  <Button type='submit' disabled={disabledButton || disabled}>
                    Simpan
                  </Button>
                </div>
              </div>
            )
          }

          return (
            <form
              className='srd:flex srd:flex-col srd:gap-y-4 srd:w-full srd:flex-1 srd:min-h-0'
              id='put-books-in-my-collection-form'
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void form.handleSubmit()
              }}
            >
              <form.Field name='selectedCollections'>
                {(field) => (
                  <fieldset
                    className='srd:flex srd:flex-col srd:gap-y-4 srd:w-full srd:flex-1 srd:min-h-0 srd:overflow-y-auto srd:min-w-0 srd:overflow-x-hidden'
                    disabled={disabled}
                  >
                    {filteredCollections.map((collection) => {
                      const checked =
                        (field.state.value as string[] | undefined)?.includes(collection.id.toString()) ?? false

                      return (
                        <label
                          key={`collection-${collection.id}`}
                          className={cn(
                            'srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:p-2 srd:rounded-lg srd:cursor-pointer srd:select-none',
                            checked && 'srd:bg-grey-10',
                          )}
                        >
                          <div className='srd:flex srd:items-center srd:gap-x-2'>
                            <div className='srd:sm:w-[100px] srd:w-[70px] srd:shrink-0'>
                              <BookGalleryCard
                                collection={collection.myBookCollectionToBooks}
                                withAnimation={false}
                                type='button'
                                tabIndex={-1}
                                className='srd:pointer-events-none'
                              />
                            </div>

                            <div className='srd:flex srd:flex-col srd:gap-y-1'>
                              <p className={cn(typographyVariants({ variant: 'body-lg' }))}>{collection.name}</p>

                              <p className={cn(typographyVariants({ variant: 'body-sm' }))}>
                                {collection.myBookCollectionToBooks.length} buku
                              </p>
                            </div>
                          </div>

                          <Checkbox
                            checked={checked}
                            onCheckedChange={(checkedValue) => {
                              const currentValue = (field.state.value as string[] | undefined) ?? []
                              const newValue = checkedValue
                                ? [...currentValue, collection.id.toString()]
                                : currentValue.filter((id) => id !== collection.id.toString())
                              field.handleChange(newValue)
                            }}
                          />
                        </label>
                      )
                    })}
                  </fieldset>
                )}
              </form.Field>

              <div className='srd:flex srd:items-center srd:justify-end srd:gap-x-4'>
                <Button
                  type='button'
                  variant='ghost'
                  disabled={disabled}
                  onClick={() => {
                    const preSelectedCollections = collectionData.data
                      .filter((collection) =>
                        collection.myBookCollectionToBooks.some((row) => row.book.id === bookData.id),
                      )
                      .map((collection) => collection.id.toString())

                    form.setFieldValue('selectedCollections', preSelectedCollections)

                    if (onCancel) onCancel()
                  }}
                >
                  Batal
                </Button>

                <Button type='submit' disabled={disabledButton || disabled}>
                  Simpan
                </Button>
              </div>
            </form>
          )
        })()}
      </section>

      <Dialog open={openMakeNewBookCollectionDialog} onOpenChange={setOpenMakeNewBookCollectionDialog} modal>
        <DialogContent className='srd:w-full srd:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle>Buat Koleksi</DialogTitle>

            <DialogDescription className='srd:sr-only'>
              Buat koleksi baru untuk memudahkan pencarian buku.
            </DialogDescription>
          </DialogHeader>

          <MyBookCollectionForm
            withAreYouSureDialog
            onFinishSubmitting={async (data) => {
              toast.success('Koleksi berhasil dibuat!')

              if (data.type === 'create') {
                setAutoCheckedIds((prev) => Array.from(new Set([...prev, data.id.toString()])))
              }

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getAllBookCollections.infiniteQueryKey(),
              })

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getAllBookCollections.queryKey(),
              })

              setOpenMakeNewBookCollectionDialog(false)
            }}
            onCancel={() => setOpenMakeNewBookCollectionDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

interface OneCollectionCreate {
  type: 'create'
  createType: 'one'
  renderLink: RenderLink
  collectionName: string
  collectionId: string
}

interface MultipleCollectionCreate {
  type: 'create'
  createType: 'multiple'
  collectionLength: number
  renderLink: RenderLink
}

interface CollectionDelete {
  type: 'delete'
}

interface CollectionToastProps {
  bookData: Pick<BookDetails, 'coverUrl' | 'title'>
  data: OneCollectionCreate | MultipleCollectionCreate | CollectionDelete
}

export function CollectionToast({ bookData, data }: CollectionToastProps) {
  if (data.type === 'delete') {
    return (
      <div className='srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:p-4'>
        <p className='srd:text-main-red-100 srd:flex srd:items-start srd:text-sm'>
          <Check className='srd:w-4 srd:h-4 srd:mr-2 srd:shrink-0' />
          {bookData.title} telah dikeluarkan dari seluruh koleksi
        </p>
      </div>
    )
  }

  if (data.createType === 'one') {
    return (
      <div className='srd:flex srd:items-center srd:justify-between srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:gap-x-4 srd:p-4'>
        <div className='srd:flex srd:items-center srd:gap-x-2'>
          <img
            src={bookData.coverUrl}
            alt={bookData.title}
            width={60}
            height={60}
            draggable={false}
            className='srd:rounded-md srd:aspect-3/4 srd:object-cover srd:overflow-clip srd:shrink-0'
          />

          <p className='srd:text-complementary-purple-100 srd:font-semibold srd:text-xs'>
            {bookData.title} tersimpan di {data.collectionName}
          </p>
        </div>

        <Button asChild variant='outline' className='srd:w-fit srd:h-fit srd:px-2 srd:py-1 srd:text-xs'>
          {data.renderLink({
            children: 'Kunjungi koleksi',
            className: 'srd:w-fit srd:h-fit srd:shrink-0',
            meta: {
              collectionId: data.collectionId,
            },
          })}
        </Button>
      </div>
    )
  }

  return (
    <div className='srd:flex srd:items-center srd:justify-between srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:gap-x-4 srd:p-4'>
      <div className='srd:flex srd:items-center srd:gap-x-2'>
        <img
          src={bookData.coverUrl}
          alt={bookData.title}
          width={60}
          height={60}
          draggable={false}
          className='srd:rounded-md srd:aspect-3/4 srd:object-cover srd:overflow-clip srd:shrink-0'
        />

        <p className='srd:text-complementary-purple-100 srd:font-semibold srd:text-xs'>
          {bookData.title} tersimpan di {data.collectionLength} koleksi
        </p>
      </div>

      <Button asChild variant='outline' className='srd:w-fit srd:h-fit srd:px-2 srd:py-1 srd:text-xs'>
        {data.renderLink({
          children: 'Kunjungi koleksi',
          className: 'srd:w-fit srd:h-fit srd:shrink-0',
        })}
      </Button>
    </div>
  )
}
